import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../auth/useUser';
import CreditIcon from './CreditIcon';

/**
 * A small widget that shows the user's available credits.
 * Replaces the static Pricing link.
 */
const CreditWidget = () => {
    const { isAuthenticated, remaining, credits, isUnlimited, isLoading, isLimitsLoading, isCreditsLoading } = useUser();

    if (isLoading) {
        return (
            <div className="h-9 w-24 bg-slate-200 dark:bg-white/5 rounded-full animate-pulse border border-slate-300 dark:border-white/10" />
        );
    }

    return (
        <Link
            to="/pricing"
            className="group flex items-center gap-3 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 hover:border-violet-500/30 hover:bg-violet-500/5 transition-all duration-300"
        >
            <div className="flex items-center gap-3">
                {/* Credits - Only show for authenticated users */}
                {isAuthenticated && (
                    <div className="flex items-center gap-2 border-r border-slate-200 dark:border-white/10 pr-3">
                        <div className="group-hover:scale-110 transition-transform">
                            <CreditIcon size="sm" />
                        </div>
                        <div className="flex flex-col items-start leading-none gap-0.5 min-w-[40px]">
                            <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                Credits
                            </span>
                            {isCreditsLoading ? (
                                <div className="h-4 w-8 bg-slate-200 dark:bg-white/10 rounded animate-pulse" />
                            ) : (
                                <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                                    {credits}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Generations */}
                <div className="flex items-center gap-2">
                    {!isAuthenticated && (
                        <div className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
                    )}
                    <div className="flex flex-col items-start leading-none gap-0.5 min-w-[60px]">
                        <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                            Generations
                        </span>
                        {isLimitsLoading ? (
                            <div className="h-4 w-12 bg-slate-200 dark:bg-white/10 rounded animate-pulse" />
                        ) : (
                            <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                                {isUnlimited ? 'Unlimited' : `${remaining} left`}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="ml-1 w-4 h-4 rounded-full bg-slate-200 dark:bg-white/10 flex items-center justify-center group-hover:bg-violet-500 group-hover:text-white transition-colors">
                <svg
                    className="w-2.5 h-2.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
            </div>
        </Link>
    );
};

export default CreditWidget;
